import React, { useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";

import moment, { type Moment } from "moment";

import AppContext from "~/common/AppContext";
import { ReactComponent as SmileEmoji } from "~/legacy-assets/images/icons/smile-emoji.svg";
import type { DayEvent } from "~/nav/panels/Meetings";
import FellowButton from "~/ui/button";

import type { ViewMode } from "../utils";

import styles from "./PanelMessage.module.css";

interface PanelMessageProps {
	noExternalCalendars: boolean;
	viewMode: ViewMode;
	loading: boolean;
	dayEvents: DayEvent[];
	start: Moment;
}

export default function PanelMessage(props: PanelMessageProps) {
	if (props.noExternalCalendars) return <NoCalendarsMessage />;
	if (props.viewMode !== "day") return null;

	const startOfDate = moment(props.start).startOf("day");
	const endOfDate = moment(props.start).endOf("day");
	const hasEvents = props.dayEvents?.some(
		edge => edge?.start && edge?.end && moment(edge.start).isBefore(endOfDate) && moment(edge.end).isAfter(startOfDate),
	);

	if (!props.loading && !hasEvents) return <NoEventsMessage />;

	return null;
}

function NoEventsMessage() {
	const history = useHistory();

	// TODO: using history.push instead of a <Link> component isn't very semantic :(
	// It would be nice to have a FellowButton that was a <Link> component underneath
	const goToActionItems = useCallback(() => history.push("/action-items/"), [history]);

	return (
		<section className={styles.messageBox}>
			<SmileEmoji />
			<div className={styles.messageText}>Looks like your day is open!</div>
			<div className={styles.messageText}>Might be a good day to tackle some action items?</div>
			<FellowButton size="wide" variant="primary" onClick={goToActionItems}>
				View Action Items
			</FellowButton>
		</section>
	);
}

function NoCalendarsMessage() {
	const { openSettingsModal } = useContext(AppContext);

	const goToConnections = useCallback(() => openSettingsModal("apps-and-integrations"), [openSettingsModal]);

	return (
		<section className={styles.messageBox}>
			<div className={styles.messageText}>You currently have no calendars connected.</div>
			<div className={styles.messageText}>
				Syncing your calendar allows your events to automatically appear in Fellow.
			</div>
			<FellowButton size="wide" variant="primary" onClick={goToConnections}>
				Connect a calendar
			</FellowButton>
		</section>
	);
}
